import { useNavigate, useParams, useLocation } from 'react-router'
import React, { useEffect, useState, useMemo } from 'react'
import { Box, Menu, MenuItem, Typography, CircularProgress, Divider } from '@mui/material'
import { styled } from '@mui/material/styles'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import DayViewCalendarComponent from 'src/clinic/components/ClinicManageBookingsPage/DayViewCalendarComponent'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { getMinimalClinicLocationData } from 'src/supportPortal/views/therapists/clinics/util'
import { getMinimalMentorData } from 'src/supportPortal/views/therapists/util'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { canRescheduleSupportBookedEvent } from 'src/supportPortal/access/accessMatrix'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'

const StyledTextContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: 0,
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    gap: theme.spacing(0.75),
  },
}))

const ClickableText = styled(Typography)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  cursor: 'pointer',
  color: 'rgba(255, 255, 255, 0.95)',
  fontWeight: 500,
  fontSize: '15px',
  transition: 'all 0.2s ease',
  '&:hover': {
    color: 'rgba(255, 255, 255, 1)',
    '& .dropdown-icon': {
      color: 'rgba(255, 255, 255, 1)',
      transform: 'translateY(1px)',
    },
  },
  '& .dropdown-icon': {
    fontSize: '16px',
    color: 'rgba(255, 255, 255, 0.7)',
    transition: 'all 0.2s ease',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    '& .dropdown-icon': {
      fontSize: '14px',
    },
  },
}))

const SeparatorText = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  fontSize: '15px',
  fontWeight: 400,
  margin: `0 ${theme.spacing(0.5)}`,
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
    margin: `0 ${theme.spacing(0.25)}`,
  },
}))

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(0, 0, 0, 0.06)',
    marginTop: theme.spacing(0.5),
    minWidth: 240,
    maxWidth: 320,
    maxHeight: 450,
    overflow: 'auto',
    padding: 0,
    // Webkit scrollbar (Chrome, Safari, Edge)
    '&::-webkit-scrollbar': {
      width: '10px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(0, 0, 0, 0.05)',
      borderRadius: '10px',
      margin: '4px 0',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(99, 102, 241, 0.3)',
      borderRadius: '10px',
      border: '2px solid transparent',
      backgroundClip: 'padding-box',
      '&:hover': {
        background: 'rgba(99, 102, 241, 0.5)',
        backgroundClip: 'padding-box',
      },
      '&:active': {
        background: 'rgba(99, 102, 241, 0.7)',
        backgroundClip: 'padding-box',
      },
    },
    // Firefox scrollbar
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(99, 102, 241, 0.3) rgba(0, 0, 0, 0.05)',
  },
}))

// Color palette for different brand groups - soothing, modern colors
const BRAND_COLORS = [
  { bg: 'rgba(99, 102, 241, 0.12)', border: 'rgba(99, 102, 241, 0.2)', text: 'rgba(67, 56, 202, 0.85)' },
  { bg: 'rgba(139, 92, 246, 0.12)', border: 'rgba(139, 92, 246, 0.2)', text: 'rgba(109, 40, 217, 0.85)' },
  { bg: 'rgba(59, 130, 246, 0.12)', border: 'rgba(59, 130, 246, 0.2)', text: 'rgba(37, 99, 235, 0.85)' },
  { bg: 'rgba(168, 85, 247, 0.12)', border: 'rgba(168, 85, 247, 0.2)', text: 'rgba(147, 51, 234, 0.85)' },
  { bg: 'rgba(236, 72, 153, 0.12)', border: 'rgba(236, 72, 153, 0.2)', text: 'rgba(219, 39, 119, 0.85)' },
  { bg: 'rgba(34, 197, 94, 0.12)', border: 'rgba(34, 197, 94, 0.2)', text: 'rgba(22, 163, 74, 0.85)' },
  { bg: 'rgba(251, 146, 60, 0.12)', border: 'rgba(251, 146, 60, 0.2)', text: 'rgba(234, 88, 12, 0.85)' },
  { bg: 'rgba(14, 165, 233, 0.12)', border: 'rgba(14, 165, 233, 0.2)', text: 'rgba(2, 132, 199, 0.85)' },
]

const BrandSectionHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'brandColor',
})(({ theme, brandColor }) => ({
  padding: theme.spacing(0.75, 1.5),
  backgroundColor: brandColor?.bg || 'rgba(99, 102, 241, 0.08)',
  borderBottom: `1px solid ${brandColor?.border || 'rgba(99, 102, 241, 0.12)'}`,
  borderTop: `1px solid ${brandColor?.border || 'rgba(99, 102, 241, 0.12)'}`,
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: `linear-gradient(90deg, transparent, ${brandColor?.border || 'rgba(99, 102, 241, 0.12)'}, transparent)`,
  },
}))

const BrandNameText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'brandColor',
})(({ theme, brandColor }) => ({
  fontSize: '10px',
  fontWeight: 500,
  color: brandColor?.text || 'rgba(99, 102, 241, 0.6)',
  textTransform: 'uppercase',
  letterSpacing: '1.2px',
  opacity: 0.65,
  margin: 0,
}))

const ClinicGroupContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'brandColor',
})(({ theme, brandColor }) => ({
  backgroundColor: brandColor?.bg || 'rgba(99, 102, 241, 0.04)',
  '&:not(:last-child)': {
    marginBottom: theme.spacing(0.25),
  },
  '&:first-of-type': {
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px',
    overflow: 'hidden',
  },
  '&:last-of-type': {
    borderBottomLeftRadius: '12px',
    borderBottomRightRadius: '12px',
    overflow: 'hidden',
  },
}))

const ClinicMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== 'brandColor',
})(({ theme, brandColor }) => ({
  paddingLeft: theme.spacing(2.5),
  paddingRight: theme.spacing(1.5),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  backgroundColor: 'transparent',
  color: brandColor?.text || 'rgba(0, 0, 0, 0.87)',
  fontSize: '14px',
  fontWeight: 500,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: brandColor?.bg || 'rgba(99, 102, 241, 0.08)',
    color: brandColor?.text || 'rgba(0, 0, 0, 0.87)',
    paddingLeft: theme.spacing(3),
  },
  '&.Mui-selected': {
    backgroundColor: brandColor?.bg || 'rgba(99, 102, 241, 0.12)',
    color: brandColor?.text || 'rgba(0, 0, 0, 0.87)',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: brandColor?.bg || 'rgba(99, 102, 241, 0.16)',
    },
  },
}))

export default function SupportMentorCalendar({
  dontOpenReports = false,
  getSingleMentorApiSignature,
  meetingBookApiSignature = '/api/secure/cc/support/therapist/meeting/book',
  bookingAllowed,
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { mentorSlug, clinicLocationSlug } = useParams()

  // Detect route pattern: practitioner-first or clinic-first
  const isPractitionerFirstRoute = location.pathname.includes('/practitioner-management/')
  const isClinicFirstRoute = location.pathname.includes('/clinics-hospitals/clinics/')

  const [selectedTherapist, setSelectedTherapist] = useState()
  const [selectedClinic, setSelectedClinic] = useState()
  const [isLoading, setIsLoading] = useState(true)
  const [availablePractitioners, setAvailablePractitioners] = useState([])
  const [availableClinics, setAvailableClinics] = useState([])
  const [loadingPractitioners, setLoadingPractitioners] = useState(false)
  const [loadingClinics, setLoadingClinics] = useState(false)
  const [practitionerMenuAnchor, setPractitionerMenuAnchor] = useState(null)
  const [clinicMenuAnchor, setClinicMenuAnchor] = useState(null)
  const [isUpdatingContent, setIsUpdatingContent] = useState(false)
  const currentUserRoles = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('authorities')) || []
    } catch (error) {
      return []
    }
  }, [])
  const canSupportRescheduleBookedSlot = useMemo(
    () => (bookedSlot) => canRescheduleSupportBookedEvent(currentUserRoles, bookedSlot?.eventType),
    [currentUserRoles],
  )

  // Fetch all practitioners (for practitioner-first routes)
  async function getAllPractitioners() {
    setLoadingPractitioners(true)
    try {
      // Determine API based on route
      let apiSignature = '/api/secure/cc/support/therapists/clinical/list'
      const isDoctorsRoute = location.pathname.includes('/practitioner-management/doctors')
      const isScreenersRoute = location.pathname.includes('/practitioner-management/screeners')
      if (isDoctorsRoute) {
        apiSignature = '/api/secure/cc/support/doctor/list'
      } else if (isScreenersRoute) {
        apiSignature = '/api/secure/cc/support/screeners/clinical/list'
      }
      
      const resp = await getSPDataWithParams(apiSignature)
      if (resp && isCommonResponseSuccessfulV2(resp)) {
        const practitioners = getDataMapFromCommonResponse(resp)?.mentor || []
        // Sort practitioners alphabetically by name
        const sortedPractitioners = practitioners.sort((a, b) => {
          const nameA = (a.name || a.title || '').toLowerCase()
          const nameB = (b.name || b.title || '').toLowerCase()
          return nameA.localeCompare(nameB)
        })
        setAvailablePractitioners(sortedPractitioners)
      }
    } catch (error) {
      console.error('Error fetching all practitioners:', error)
      setAvailablePractitioners([])
    } finally {
      setLoadingPractitioners(false)
    }
  }

  // Fetch practitioners for a clinic location (for clinic-first routes)
  async function getPractitionersForClinic(clinicSlug) {
    setLoadingPractitioners(true)
    try {
      // TODO: Replace with the dedicated API endpoint when provided
      // Expected API: GET /api/secure/cc/support/clinic/:clinicSlug/practitioners
      // For now, using the clinic data to extract practitioners from doctors/therapists/screeners fields
      const clinicData = await getMinimalClinicLocationData(clinicSlug)
      if (clinicData) {
        // Extract practitioner IDs from clinic location
        const doctorIds = (clinicData.doctors || []).map((d) => d?.uid || d?.id).filter(Boolean)
        const therapistIds = (clinicData.therapists || []).map((t) => t?.uid || t?.id).filter(Boolean)
        const screenerIds = (clinicData.screeners || []).map((s) => s?.uid || s?.id).filter(Boolean)
        
        const allPractitionerIds = [...doctorIds, ...therapistIds, ...screenerIds]
        
        if (allPractitionerIds.length > 0) {
          // Fetch all mentors and filter by IDs
          const resp = await getSPDataWithParams('/api/secure/cc/support/mentors/list', {
            mentorEventTypes: 'therapy-session,consultation',
            sendAssociatedChambers: false,
          })
          
          if (resp && isCommonResponseSuccessfulV2(resp)) {
            const allMentors = getDataMapFromCommonResponse(resp)?.mentor || []
            const filtered = allMentors.filter((mentor) => 
              allPractitionerIds.includes(mentor.id) || 
              allPractitionerIds.includes(mentor.uid) ||
              allPractitionerIds.includes(String(mentor.id)) ||
              allPractitionerIds.includes(String(mentor.uid))
            )
            // Sort practitioners alphabetically by name
            const sortedPractitioners = filtered.sort((a, b) => {
              const nameA = (a.name || a.title || '').toLowerCase()
              const nameB = (b.name || b.title || '').toLowerCase()
              return nameA.localeCompare(nameB)
            })
            setAvailablePractitioners(sortedPractitioners)
          }
        } else {
          setAvailablePractitioners([])
        }
      }
    } catch (error) {
      console.error('Error fetching practitioners for clinic:', error)
      setAvailablePractitioners([])
    } finally {
      setLoadingPractitioners(false)
    }
  }

  // Fetch clinics for a practitioner
  async function getClinicsForPractitioner(mentorSlug) {
    setLoadingClinics(true)
    try {
      const isScreenersRoute = location.pathname.includes('/practitioner-management/screeners')
      const clinicAssociationsApiSignature = isScreenersRoute
        ? '/api/secure/cc/support/screeners/clinic-associations'
        : '/api/secure/cc/support/therapists/clinic-associations'
      const resp = await getSPDataWithParams(clinicAssociationsApiSignature, {
        'mentor-slug': mentorSlug,
      })
      if (resp && isCommonResponseSuccessfulV2(resp)) {
        const clinics = getDataMapFromCommonResponse(resp)?.clinic || []
        setAvailableClinics(clinics)
      }
    } catch (error) {
      console.error('Error fetching clinics for practitioner:', error)
      setAvailableClinics([])
    } finally {
      setLoadingClinics(false)
    }
  }

  async function getSelectedTherapist(slug = mentorSlug) {
    if (!slug) return
    const resp = await getMinimalMentorData(slug, undefined, getSingleMentorApiSignature)
    setSelectedTherapist(resp)
    return resp
  }

  async function getSelectedClinic(slug = clinicLocationSlug) {
    if (!slug) return
    const resp = await getMinimalClinicLocationData(slug)
    setSelectedClinic(resp)
    return resp
  }

  async function Setup(skipLoading = false) {
    if (!skipLoading) {
    setIsLoading(true)
    }
    
    if (isPractitionerFirstRoute) {
      // For practitioner-first routes, fetch all practitioners first (only if not already loaded)
      if (availablePractitioners.length === 0) {
        await getAllPractitioners()
      }
      // Load practitioner first, then their clinics
      const therapist = await getSelectedTherapist()
      if (therapist) {
        await getClinicsForPractitioner(mentorSlug)
      }
      await getSelectedClinic()
    } else if (isClinicFirstRoute) {
      // Load clinic first, then its practitioners
      const clinic = await getSelectedClinic()
      if (clinic) {
        await getPractitionersForClinic(clinicLocationSlug)
      }
      await getSelectedTherapist()
    } else {
      // Fallback: load both
      await Promise.all([getSelectedTherapist(), getSelectedClinic()])
    }
    
    if (!skipLoading) {
    setIsLoading(false)
    }
  }

  useEffect(() => {
    // Only run full setup if we're not currently updating content
    if (!isUpdatingContent) {
    Setup()
    }
  }, [mentorSlug, clinicLocationSlug])

  const handlePractitionerClick = (event) => {
    setPractitionerMenuAnchor(event.currentTarget)
  }

  const handleClinicClick = (event) => {
    setClinicMenuAnchor(event.currentTarget)
  }

  const handlePractitionerMenuClose = () => {
    setPractitionerMenuAnchor(null)
  }

  const handleClinicMenuClose = () => {
    setClinicMenuAnchor(null)
  }

  const handlePractitionerChange = async (newMentorSlug) => {
    handlePractitionerMenuClose()
    setIsUpdatingContent(true)
    
    try {
      // Load new practitioner data first (before URL update for smoother transition)
      const newTherapist = await getSelectedTherapist(newMentorSlug)
      
      // Update URL smoothly without page reload
      if (isPractitionerFirstRoute) {
        // Determine if it's doctors or therapists route
        const isDoctorsRoute = location.pathname.includes('/practitioner-management/doctors')
        const basePath = isDoctorsRoute 
          ? '/support-home/practitioner-management/doctors'
          : '/support-home/practitioner-management/therapists'
        
        // If clinic is selected, keep it; otherwise navigate to practitioner's clinic listing
        const newPath = clinicLocationSlug
          ? `${basePath}/${newMentorSlug}/${clinicLocationSlug}`
          : `${basePath}/${newMentorSlug}`
        navigate(newPath, { replace: false })
        
        if (newTherapist) {
          // Always fetch clinics for the new practitioner
          await getClinicsForPractitioner(newMentorSlug)
          // If clinic was selected, verify it's still valid for this practitioner
          if (clinicLocationSlug) {
            await getSelectedClinic(clinicLocationSlug)
          }
        }
      } else {
        const newPath = `/support-home/clinics-hospitals/clinics/${clinicLocationSlug}/${newMentorSlug}`
        navigate(newPath, { replace: false })
      }
    } catch (error) {
      console.error('Error updating practitioner:', error)
    } finally {
      setIsUpdatingContent(false)
    }
  }

  const handleClinicChange = async (newClinicSlug) => {
    handleClinicMenuClose()
    setIsUpdatingContent(true)
    
    try {
      // Load new clinic data first (before URL update for smoother transition)
      const newClinic = await getSelectedClinic(newClinicSlug)
      
      // Update URL smoothly without page reload
      if (isPractitionerFirstRoute) {
        const newPath = `/support-home/practitioner-management/therapists/${mentorSlug}/${newClinicSlug}`
        navigate(newPath, { replace: false })
      } else {
        const newPath = `/support-home/clinics-hospitals/clinics/${newClinicSlug}/${mentorSlug}`
        navigate(newPath, { replace: false })
        
        // Load practitioners for the new clinic
        if (newClinic && mentorSlug) {
          await getPractitionersForClinic(newClinicSlug)
        }
      }
    } catch (error) {
      console.error('Error updating clinic:', error)
    } finally {
      setIsUpdatingContent(false)
    }
  }

  // Get clinic display name (without brand)
  const getClinicDisplayName = (clinic) => {
    return clinic?.title || clinic?.name || 'Unknown Clinic'
  }

  // Group clinics by brand with color assignment
  const groupedClinics = useMemo(() => {
    if (!availableClinics.length) return []
    
    const grouped = {}
    availableClinics.forEach((clinic) => {
      const brandId = clinic?.clinicBrandId || 'unknown'
      const brandName = clinic?.clinicBrandName || 'Unknown Brand'
      
      if (!grouped[brandId]) {
        grouped[brandId] = {
          brandId,
          brandName,
          clinics: [],
        }
      }
      grouped[brandId].clinics.push(clinic)
    })
    
    // Convert to array, sort by brand name, and assign colors
    return Object.values(grouped)
      .sort((a, b) => a.brandName.localeCompare(b.brandName))
      .map((group, index) => ({
        ...group,
        color: BRAND_COLORS[index % BRAND_COLORS.length],
      }))
  }, [availableClinics])

  return (
    <>
      <SupportHeader showBackButton>
        <StyledTextContainer>
          {/* Practitioner Name with Dropdown */}
          <ClickableText
            onClick={handlePractitionerClick}
            sx={{ 
              opacity: isLoading || loadingPractitioners ? 0.6 : 1,
              pointerEvents: isLoading || loadingPractitioners ? 'none' : 'auto',
            }}
          >
            {isLoading || loadingPractitioners ? (
              <CircularProgress size={14} sx={{ mr: 0.5 }} />
            ) : (
              selectedTherapist?.name || selectedTherapist?.title || 'Select Practitioner'
            )}
            <KeyboardArrowDownIcon className="dropdown-icon" />
          </ClickableText>

          <SeparatorText>at</SeparatorText>

          {/* Clinic Name with Dropdown */}
          <ClickableText
            onClick={handleClinicClick}
            sx={{ 
              opacity: isLoading || loadingClinics ? 0.6 : 1,
              pointerEvents: isLoading || loadingClinics ? 'none' : 'auto',
            }}
          >
            {isLoading || loadingClinics ? (
              <CircularProgress size={14} sx={{ mr: 0.5 }} />
            ) : (
              getClinicDisplayName(selectedClinic) || 'Select Clinic'
            )}
            <KeyboardArrowDownIcon className="dropdown-icon" />
          </ClickableText>

          {/* Practitioner Menu */}
          <StyledMenu
            anchorEl={practitionerMenuAnchor}
            open={Boolean(practitionerMenuAnchor)}
            onClose={handlePractitionerMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                maxHeight: 450,
                overflow: 'auto',
                scrollBehavior: 'smooth',
              },
            }}
          >
            {loadingPractitioners ? (
              <MenuItem disabled>
                <CircularProgress size={20} />
              </MenuItem>
            ) : availablePractitioners.length > 0 ? (
              availablePractitioners.map((practitioner) => (
                <MenuItem
                  key={practitioner.slug}
                  onClick={() => handlePractitionerChange(practitioner.slug)}
                  selected={practitioner.slug === selectedTherapist?.slug}
                >
                  {practitioner.name || practitioner.title}
                </MenuItem>
              ))
            ) : selectedTherapist ? (
              <MenuItem
                onClick={() => handlePractitionerChange(selectedTherapist.slug)}
                selected
              >
                {selectedTherapist.name || selectedTherapist.title}
              </MenuItem>
            ) : (
              <MenuItem disabled>No practitioners available</MenuItem>
            )}
          </StyledMenu>

          {/* Clinic Menu */}
          <StyledMenu
            anchorEl={clinicMenuAnchor}
            open={Boolean(clinicMenuAnchor)}
            onClose={handleClinicMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                maxHeight: 450,
                overflow: 'auto',
                // Ensure smooth scrolling
                scrollBehavior: 'smooth',
                // Better scrollbar visibility
                '&::-webkit-scrollbar': {
                  width: '10px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'rgba(0, 0, 0, 0.05)',
                  borderRadius: '10px',
                  margin: '4px 0',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(99, 102, 241, 0.3)',
                  borderRadius: '10px',
                  border: '2px solid transparent',
                  backgroundClip: 'padding-box',
                  '&:hover': {
                    background: 'rgba(99, 102, 241, 0.5)',
                    backgroundClip: 'padding-box',
                  },
                },
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(99, 102, 241, 0.3) rgba(0, 0, 0, 0.05)',
              },
            }}
          >
            {loadingClinics ? (
              <MenuItem disabled>
                <CircularProgress size={20} />
              </MenuItem>
            ) : groupedClinics.length > 0 ? (
              groupedClinics.map((group) => (
                <ClinicGroupContainer key={group.brandId} brandColor={group.color}>
                  <BrandSectionHeader brandColor={group.color}>
                    <BrandNameText brandColor={group.color}>{group.brandName}</BrandNameText>
                  </BrandSectionHeader>
                  {group.clinics.map((clinic) => (
                    <ClinicMenuItem
                      key={clinic.slug}
                      onClick={() => handleClinicChange(clinic.slug)}
                      selected={clinic.slug === selectedClinic?.slug}
                      brandColor={group.color}
                    >
                      {getClinicDisplayName(clinic)}
                    </ClinicMenuItem>
                  ))}
                </ClinicGroupContainer>
              ))
            ) : selectedClinic ? (
              <MenuItem
                onClick={() => handleClinicChange(selectedClinic.slug)}
                selected
              >
                {getClinicDisplayName(selectedClinic)}
              </MenuItem>
            ) : (
              <MenuItem disabled>No clinics available</MenuItem>
            )}
          </StyledMenu>
        </StyledTextContainer>
      </SupportHeader>
      <Box
        sx={{
          width: '100%',
          padding: { xs: 1, sm: 1.5 },
          background: 'linear-gradient(180deg, rgba(249, 250, 251, 0.5) 0%, rgba(243, 244, 246, 0.3) 100%)',
          minHeight: 'calc(100vh - 80px)',
          overflowX: 'visible',
        }}
      >
        <Box sx={{ 
          maxWidth: '100%', 
          margin: '0 auto', 
          width: '100%', 
          padding: { xs: '0 8px', sm: '0 12px', md: '0 16px', lg: '0 24px' }, 
          overflowX: 'visible' 
        }}>
          <DayViewCalendarComponent
            mentorId={selectedTherapist?.id}
            mentorProfileData={selectedTherapist}
            thisClinicData={selectedClinic}
            transformClinicBrandData={(clinicBrandData) => {
              return {
                ...clinicBrandData,
                bookingFormSettings: null,
              }
            }}
            bookingLisitingApiSignature={`/api/secure/cc/support/events/booked-slots/all-with-patient-info?mentorId=${selectedTherapist?.id}&include-report-related-data=true`}
            meetingBookApiSignature={meetingBookApiSignature}
            // patientSearchApiSignature={`?clinicId=${selectedClinic?.id}&clinicBrandId=${selectedClinic?.clinicBrandId}`}
            patientSearchApiSignature={`/api/secure/cc/support/patient/search`}
            cancelAppointmentApiSignature={`/api/secure/cc/support/therapist/meeting/booked-slot/cancel`}
            editBookingApiSignature={`/api/secure/cc/support/events/edit`}
            pastPatientBookingsApiSignature={`/api/secure/cc/support/therapist/meeting/booked-slots/patient`}
            createPatientApiSignature={`/api/secure/cc/support/patient/new`}
            patientUpdateApiSignature={`/api/secure/cc/support/patient/update`}
            patientTagsApiSignature={`/api/secure/cc/support/patient/tags`}
            listOfPaymentLinksApiSignature={`/api/secure/cc/support/events/user-payment-link/get`}
            publicBookingJoiningLinksApiSignature={`/api/secure/cc/support/events/video-session-tokens/get`}
            getPrescriptionImagesApiSignature={`/api/secure/cc/support/csm/emr-reports/prescription/images/get-by-eventId`}
            savePrescriptionImagesApiSignature={`/api/secure/cc/support/csm/emr-reports/prescription/images/save`}
            fileUploadApiSignature={`/api/secure/cc/support/csm/emr-reports/file/upload`}
            sendPrescriptionPdfApiSignature={`/api/secure/cc/support/doctor/prescription/generate/pdf`}
            showGalleryForPrescriptionImages
            forMentor
            
            hideToggleSwitchForReportRelatedData={false}
            hideMarkedCompletedButton
            showCancelledChipInHeader
            hideStartButton 
            forceHoldingSlot
            hideBookButton={bookingAllowed !== undefined ? !bookingAllowed : !getAllRolesInBoolean().isL3TherapySupport}
            showSendingLinkButtonsToSupport
            showViewReportsButtonInActionButtons={!dontOpenReports}
            hideRescheduleDialog={false}
            canRescheduleBookedSlot={canSupportRescheduleBookedSlot}
            rescheduleDialogType="support"
          />
        </Box>
      </Box>
    </>
  )
}
